import React, { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Search as SearchIcon, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ImagePickerDialog({ open, onClose, onSelect, defaultQuery = "" }) {
  const [library, setLibrary] = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const [query, setQuery] = useState(defaultQuery);
  const [unsplashResults, setUnsplashResults] = useState([]);
  const [unsplashConfigured, setUnsplashConfigured] = useState(true);
  const [searching, setSearching] = useState(false);

  const loadLibrary = useCallback(async () => {
    setLibLoading(true);
    try {
      const { data } = await api.get("/images/library");
      setLibrary(data || []);
    } finally {
      setLibLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadLibrary();
      setQuery(defaultQuery);
      setUnsplashResults([]);
    }
  }, [open, defaultQuery, loadLibrary]);

  const searchUnsplash = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.get(`/images/search?query=${encodeURIComponent(query)}&per_page=12`);
      setUnsplashConfigured(!!data.configured);
      setUnsplashResults(data.results || []);
      if (!data.configured) {
        toast.message("Unsplash not configured — see Settings");
      } else if ((data.results || []).length === 0) {
        toast.message("No results");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unsplash search failed");
    } finally {
      setSearching(false);
    }
  };

  const pickLibrary = (img) => {
    onSelect(img.data_url);
    onClose();
  };

  const pickUnsplash = async (img) => {
    // Persist into library so admin can re-use, then return the URL
    try {
      await api.post("/images/library", {
        data_url: img.regular,
        prompt: query,
        source: "unsplash",
        external_url: img.unsplash_url,
        attribution: `Photo by ${img.author} on Unsplash`,
      });
    } catch {
      /* non-fatal */
    }
    onSelect(img.regular);
    onClose();
  };

  const deleteLibrary = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/images/library/${id}`);
      setLibrary((prev) => prev.filter((i) => i.id !== id));
      toast.success("Removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="image-picker-dialog"
        className="rounded-none bg-ink border-hairline text-bone max-w-3xl max-h-[85vh] overflow-y-auto scrollbar-thin"
      >
        <DialogHeader>
          <DialogTitle className="font-display tracking-tightest text-bone">Pick a cover image</DialogTitle>
          <DialogDescription className="label-mono text-[10px] text-bone/50">
            Reuse a previously generated cover or search free stock photos on Unsplash.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="library">
          <TabsList className="rounded-none bg-surface border border-hairline p-0 h-auto">
            <TabsTrigger value="library" data-testid="img-tab-library" className="rounded-none data-[state=active]:bg-bone data-[state=active]:text-ink label-mono px-4 py-2">
              My library ({library.length})
            </TabsTrigger>
            <TabsTrigger value="unsplash" data-testid="img-tab-unsplash" className="rounded-none data-[state=active]:bg-bone data-[state=active]:text-ink label-mono px-4 py-2">
              Unsplash
            </TabsTrigger>
          </TabsList>

          <TabsContent value="library" className="mt-4">
            {libLoading && <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
            {!libLoading && library.length === 0 && (
              <div className="text-center py-12 text-bone/50 label-mono text-xs">
                No images yet. Generate one in the composer — it'll auto-save here.
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {library.map((img) => (
                <button
                  key={img.id}
                  onClick={() => pickLibrary(img)}
                  data-testid={`lib-image-${img.id}`}
                  className="group relative border border-hairline overflow-hidden hover:border-safety transition"
                >
                  <img src={img.data_url} alt={img.prompt} className="w-full h-32 object-cover" />
                  <div className="absolute bottom-0 left-0 right-0 bg-ink/85 px-2 py-1 label-mono text-[9px] text-bone/80 text-left line-clamp-1">
                    {img.source} · {img.prompt || "—"}
                  </div>
                  <span
                    onClick={(e) => deleteLibrary(img.id, e)}
                    data-testid={`lib-image-delete-${img.id}`}
                    className="absolute top-1 right-1 p-1 bg-ink/80 text-bone/70 hover:text-safety opacity-0 group-hover:opacity-100 cursor-pointer"
                  >
                    <Trash2 className="w-3 h-3" />
                  </span>
                </button>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="unsplash" className="mt-4">
            <div className="flex gap-2">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchUnsplash()}
                placeholder="e.g. factory automation, cargo ship, robot arm…"
                className="rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-10"
                data-testid="unsplash-query-input"
              />
              <Button
                onClick={searchUnsplash}
                disabled={searching || !query.trim()}
                data-testid="unsplash-search-btn"
                className="rounded-none bg-klein hover:bg-klein-hover text-white label-mono h-10"
              >
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <SearchIcon className="w-4 h-4" />}
              </Button>
            </div>
            {!unsplashConfigured && (
              <div className="mt-3 border border-yellow-500/40 bg-yellow-500/5 p-3 text-xs text-yellow-100">
                Unsplash search needs an API key. Add <code>UNSPLASH_ACCESS_KEY</code> to <code>backend/.env</code> (free key at{" "}
                <a href="https://unsplash.com/developers" target="_blank" rel="noreferrer" className="underline">unsplash.com/developers</a>).
              </div>
            )}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {unsplashResults.map((img) => (
                <button
                  key={img.id}
                  onClick={() => pickUnsplash(img)}
                  data-testid={`unsplash-image-${img.id}`}
                  className="group relative border border-hairline overflow-hidden hover:border-safety transition text-left"
                >
                  <img src={img.thumb} alt={img.alt_description} className="w-full h-32 object-cover" />
                  <div className="absolute bottom-0 left-0 right-0 bg-ink/85 px-2 py-1 label-mono text-[9px] text-bone/80 line-clamp-1">
                    by {img.author}
                  </div>
                  <a
                    href={img.unsplash_url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="absolute top-1 right-1 p-1 bg-ink/80 text-bone/70 hover:text-bone opacity-0 group-hover:opacity-100"
                    aria-label="View on Unsplash"
                  >
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </button>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
